import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Row, Col } from 'reactstrap';
import { AssistanceDTO } from '../../models/Assistance/AssistanceDTO';
import { AssistanceService } from '../../services/AssistanceService';
import { LookupService } from '../../services/LookupService';
import ButtonComponent from '../ButtonComponent';
import InputComponent from '../InputComponent';
import SelectComponent from '../SelectComponent';
import { SelectItemsProps } from '../Select2Component';
import Toast from '../../helpers/Toast';

interface AssistanceCanonSummaryComponentProps {
    assistance: AssistanceDTO;
    setAssistance: React.Dispatch<React.SetStateAction<AssistanceDTO>>;
    isLoadingAssistance: boolean;
}

interface CanonSummaryFormData {
    activityStatus: string;
    unsuccessfulReason: string;
    notes: string;
    workStart: string;
    workEnd: string;
    purpose: string;
    fault: string;
    location: string;
    reason: string;
    correction: string;
    equipmentStatus: string;
    partsUsed: string;
    counterReadings: string;
}

// Create service instances outside component to avoid recreating on each render
const assistanceService = new AssistanceService();
const lookupService = new LookupService();

export function AssistanceCanonSummaryComponent({
    assistance,
    setAssistance,
    isLoadingAssistance
}: AssistanceCanonSummaryComponentProps) {
    const intl = useIntl();

    // Loading state for Canon summary data
    const [isLoadingCanonData, setIsLoadingCanonData] = useState(false);
    
    // Loading state for lookup data
    const [isLoadingLookups, setIsLoadingLookups] = useState(false);
    
    // Loading state for email sending
    const [isLoadingEmail, setIsLoadingEmail] = useState(false);

    // Lookup data
    const [activityStatusOptions, setActivityStatusOptions] = useState<SelectItemsProps[]>([]);
    const [unsuccessfulReasonOptions, setUnsuccessfulReasonOptions] = useState<SelectItemsProps[]>([]);
    const [purposeOptions, setPurposeOptions] = useState<SelectItemsProps[]>([]);
    const [faultOptions, setFaultOptions] = useState<SelectItemsProps[]>([]);
    const [locationOptions, setLocationOptions] = useState<SelectItemsProps[]>([]);
    const [reasonOptions, setReasonOptions] = useState<SelectItemsProps[]>([]);
    const [correctionOptions, setCorrectionOptions] = useState<SelectItemsProps[]>([]);
    const [equipmentStatusOptions, setEquipmentStatusOptions] = useState<SelectItemsProps[]>([]);

    // Form data state
    const [formData, setFormData] = useState<CanonSummaryFormData>({
        activityStatus: '',
        unsuccessfulReason: '',
        notes: '',
        workStart: '',
        workEnd: '',
        purpose: '',
        fault: '',
        location: '',
        reason: '',
        correction: '',
        equipmentStatus: '',
        partsUsed: '',
        counterReadings: ''
    });

    // Load lookup data when component mounts
    useEffect(() => {
        const loadLookupData = async () => {
            setIsLoadingLookups(true);
            try {
                // Load Activity Status options
                const activityStatusResponse = await lookupService.GetLookupsByType('ActivityStatus');
                if (activityStatusResponse.success && activityStatusResponse.obj) {
                    const activityStatusItems: SelectItemsProps[] = activityStatusResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setActivityStatusOptions(activityStatusItems);
                }

                // Load Unsuccessful Reason options
                const unsuccessfulReasonResponse = await lookupService.GetLookupsByType('UnsuccessfulReason');
                if (unsuccessfulReasonResponse.success && unsuccessfulReasonResponse.obj) {
                    const unsuccessfulReasonItems: SelectItemsProps[] = unsuccessfulReasonResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setUnsuccessfulReasonOptions(unsuccessfulReasonItems);
                }

                // Load Purpose options
                const purposeResponse = await lookupService.GetLookupsByType('Purpose');
                if (purposeResponse.success && purposeResponse.obj) {
                    const purposeItems: SelectItemsProps[] = purposeResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setPurposeOptions(purposeItems);
                }

                // Load Fault options
                const faultResponse = await lookupService.GetLookupsByType('Fault');
                if (faultResponse.success && faultResponse.obj) {
                    const faultItems: SelectItemsProps[] = faultResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setFaultOptions(faultItems);
                }

                // Load Location options
                const locationResponse = await lookupService.GetLookupsByType('Location');
                if (locationResponse.success && locationResponse.obj) {
                    const locationItems: SelectItemsProps[] = locationResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setLocationOptions(locationItems);
                }

                // Load Reason options
                const reasonResponse = await lookupService.GetLookupsByType('Reason');
                if (reasonResponse.success && reasonResponse.obj) {
                    const reasonItems: SelectItemsProps[] = reasonResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setReasonOptions(reasonItems);
                }

                // Load Correction options
                const correctionResponse = await lookupService.GetLookupsByType('Correction');
                if (correctionResponse.success && correctionResponse.obj) {
                    const correctionItems: SelectItemsProps[] = correctionResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setCorrectionOptions(correctionItems);
                }

                // Load Equipment Status options
                const equipmentStatusResponse = await lookupService.GetLookupsByType('EquipmentStatus');
                if (equipmentStatusResponse.success && equipmentStatusResponse.obj) {
                    const equipmentStatusItems: SelectItemsProps[] = equipmentStatusResponse.obj.map(lookup => ({
                        value: lookup.description,
                        label: lookup.description
                    }));
                    setEquipmentStatusOptions(equipmentStatusItems);
                }
            } catch (error) {
                console.error('Error loading lookup data:', error);
            } finally {
                setIsLoadingLookups(false);
            }
        };

        loadLookupData();
    }, []);

    // Load Canon summary data when assistance is available
    useEffect(() => {
        const loadCanonSummaryData = async () => {
            if (!assistance?.id || isLoadingAssistance) {
                return;
            }

            setIsLoadingCanonData(true);
            try {
                const response = await assistanceService.GetCanonSummaryData(assistance.id);
                
                if (response.success && response.obj) {
                    // Pre-populate fields with data from backend
                    setFormData(prev => ({
                        ...prev,
                        workStart: response.obj!.workStart !== "N/A" ? response.obj!.workStart : '',
                        workEnd: response.obj!.workEnd !== "N/A" ? response.obj!.workEnd : '',
                        partsUsed: response.obj!.partsUsed !== "N/A" ? response.obj!.partsUsed : '',
                        counterReadings: response.obj!.counterReadings !== "N/A" ? response.obj!.counterReadings : ''
                    }));
                }
            } catch (error) {
                console.error('Error loading Canon summary data:', error);
                // Note: In a real application, you might want to show a toast notification here
            } finally {
                setIsLoadingCanonData(false);
            }
        };

        loadCanonSummaryData();
    }, [assistance?.id, isLoadingAssistance]);

    const handleFieldChange = (field: keyof CanonSummaryFormData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = () => {
        // TODO: Implement save functionality in Phase 5
        console.log('Save Canon Summary - to be implemented', formData);
    };

    const handleSendEmail = async () => {
        if (!assistance?.id || isLoadingEmail) {
            return;
        }

        setIsLoadingEmail(true);
        try {
            const response = await assistanceService.SendCanonSummaryEmail(assistance.id);
            
            if (response.success) {
                Toast.Show("success", response.message || "Email do Canon Summary enviado com sucesso");
            } else {
                Toast.Show("error", response.message || "Erro ao enviar email do Canon Summary");
            }
        } catch (error) {
            console.error('Error sending Canon Summary email:', error);
            Toast.Show("error", "Erro inesperado ao enviar email do Canon Summary");
        } finally {
            setIsLoadingEmail(false);
        }
    };

    // Show loading state while assistance, Canon data, or lookups are loading
    const isLoading = isLoadingAssistance || isLoadingCanonData;
    
    // Determine if Send Email button should be enabled
    const isEmailButtonEnabled = !isLoading && !isLoadingEmail && assistance?.id;
    
    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="pageComponent">
            <label style={{ fontWeight: "bold" }}>
                {intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Title" })}
            </label>
            
            {/* First row - Activity Status and Unsuccessful Reason */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={activityStatusOptions}
                    selectedItemValue={formData.activityStatus}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.ActivityStatus" })}
                    onChange={(value: string) => handleFieldChange('activityStatus', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-activity-status"
                />
                
                {/* Conditional Unsuccessful Reason dropdown - only show when Activity Status is "Complete - Unsuccessful" */}
                {formData.activityStatus === "Complete - Unsuccessful" && (
                    <SelectComponent
                        xl={6}
                        lg={6}
                        md={12}
                        sm={12}
                        xs={12}
                        items={unsuccessfulReasonOptions}
                        selectedItemValue={formData.unsuccessfulReason}
                        label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.UnsuccessfulReason" })}
                        onChange={(value: string) => handleFieldChange('unsuccessfulReason', value)}
                        showLoadingSkeleton={isLoading || isLoadingLookups}
                        loading={isLoadingLookups}
                        data-testid="canon-summary-unsuccessful-reason"
                    />
                )}
            </Row>

            {/* Notes - Full width */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={12}
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    type="textArea"
                    value={formData.notes}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Notes" })}
                    onChange={(value: string) => handleFieldChange('notes', value)}
                    showLoadingSkeleton={isLoading}
                    rows={3}
                    data-testid="canon-summary-notes"
                />
            </Row>

            {/* Work Start and End dates */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    type="date"
                    value={formData.workStart}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.WorkStart" })}
                    onChange={(value: string) => handleFieldChange('workStart', value)}
                    showLoadingSkeleton={isLoading}
                    data-testid="canon-summary-work-start"
                />
                
                <InputComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    type="date"
                    value={formData.workEnd}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.WorkEnd" })}
                    onChange={(value: string) => handleFieldChange('workEnd', value)}
                    showLoadingSkeleton={isLoading}
                    data-testid="canon-summary-work-end"
                />
            </Row>

            {/* Purpose and Fault */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={purposeOptions}
                    selectedItemValue={formData.purpose}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Purpose" })}
                    onChange={(value: string) => handleFieldChange('purpose', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-purpose"
                />
                
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={faultOptions}
                    selectedItemValue={formData.fault}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Fault" })}
                    onChange={(value: string) => handleFieldChange('fault', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-fault"
                />
            </Row>

            {/* Location and Reason */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={locationOptions}
                    selectedItemValue={formData.location}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Location" })}
                    onChange={(value: string) => handleFieldChange('location', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-location"
                />
                
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={reasonOptions}
                    selectedItemValue={formData.reason}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Reason" })}
                    onChange={(value: string) => handleFieldChange('reason', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-reason"
                />
            </Row>

            {/* Correction and Equipment Status */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={correctionOptions}
                    selectedItemValue={formData.correction}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.Correction" })}
                    onChange={(value: string) => handleFieldChange('correction', value)}
                    showLoadingSkeleton={isLoading || isLoadingLookups}
                    loading={isLoadingLookups}
                    data-testid="canon-summary-correction"
                />
                
                <SelectComponent
                    xl={6}
                    lg={6}
                    md={12}
                    sm={12}
                    xs={12}
                    items={equipmentStatusOptions}
                    selectedItemValue={formData.equipmentStatus}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.EquipmentStatus" })}
                    onChange={(value: string) => handleFieldChange('equipmentStatus', value)}
                    loading={isLoadingLookups}
                    showLoadingSkeleton={isLoading}
                    data-testid="canon-summary-equipment-status"
                />
            </Row>

            {/* Parts Used - Full width textarea */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={12}
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    type="textArea"
                    value={formData.partsUsed}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.PartsUsed" })}
                    onChange={(value: string) => handleFieldChange('partsUsed', value)}
                    showLoadingSkeleton={isLoading}
                    rows={4}
                    data-testid="canon-summary-parts-used"
                />
            </Row>

            {/* Counter Readings - Full width textarea */}
            <Row style={{ margin: "0px", padding: "0px" }}>
                <InputComponent
                    xl={12}
                    lg={12}
                    md={12}
                    sm={12}
                    xs={12}
                    type="textArea"
                    value={formData.counterReadings}
                    label={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.CounterReadings" })}
                    onChange={(value: string) => handleFieldChange('counterReadings', value)}
                    showLoadingSkeleton={isLoading}
                    rows={4}
                    data-testid="canon-summary-counter-readings"
                />
            </Row>

            {/* Buttons */}
            <Row style={{ margin: "0px", padding: "0px", justifyContent: "end" }}>
                <Col xl={3} lg={3} md={6} sm={12} xs={12}>
                    <ButtonComponent
                        text={intl.formatMessage({ id: "Screens.Assistances.CanonSummary.SaveButton" })}
                        onClick={handleSave}
                        disabled={true} // Will be enabled in Phase 5
                        style={{ width: "100%", marginTop: "10px", marginRight: "10px" }}
                        data-testid="canon-summary-save-button"
                    />
                </Col>
                <Col xl={3} lg={3} md={6} sm={12} xs={12}>
                    <ButtonComponent
                        text={isLoadingEmail 
                            ? intl.formatMessage({ id: "Screens.Assistances.CanonSummary.SendingEmailButton" })
                            : intl.formatMessage({ id: "Screens.Assistances.CanonSummary.SendEmailButton" })
                        }
                        onClick={handleSendEmail}
                        disabled={!isEmailButtonEnabled}
                        style={{ width: "100%", marginTop: "10px" }}
                        data-testid="canon-summary-send-email-button"
                    />
                </Col>
            </Row>
        </div>
    );
}
